<template>
    <ModalContent
        class="invoice-modal general-settings-reference stitch-form"
        flat-chrome
        :fill-height="isSplitLayout"
        :help-content-id="`popup-editcustomerinvoice-${current_entity_profile}`"
    >
        <template slot="title">{{ title }}</template>
        <template slot="rawcontent">
            <div
                class="customerinvoice-layout"
                :class="{
                    'customerinvoice-layout--no-document': !showDocument,
                }"
                v-if="invoice"
            >
                <div
                    v-if="showDocument"
                    class="document d-flex justify-center"
                    :class="{ 'document--upload': !hasFilePreview }"
                >
                    <template v-if="billingaccount_loading">
                        <Loading :absolute="false" />
                    </template>
                    <template v-else-if="isCustomerInvoice && !billingaccount">
                        <Placeholder
                            icon="customer"
                            :no-padding="true"
                            :title="$translate('selectCustomer')"
                            :description="
                                $translate('selectCustomerDescription')
                            "
                            :actions="
                                billingaccount
                                    ? [
                                          {
                                              text: $translate(
                                                  'configureBillingaccount'
                                              ),
                                              action: 'configure',
                                          },
                                      ]
                                    : []
                            "
                            @configure="showBillingAccountSettings"
                        />
                    </template>
                    <template
                        v-else-if="
                            isCustomerInvoice && billingaccount?.handle_invoices
                        "
                    >
                        <Placeholder
                            icon="customerinvoice"
                            :no-padding="true"
                            :title="$translate('automaticInvoice')"
                            :description="
                                $translate('automaticInvoiceDescription')
                            "
                            :actions="[
                                {
                                    text: $translate('configureBillingaccount'),
                                    action: 'configure',
                                },
                            ]"
                            @configure="showBillingAccountSettings"
                        />
                    </template>
                    <template v-else-if="isCustomerInvoice">
                        <Placeholder
                            icon="customerinvoice"
                            :no-padding="true"
                            :title="$translate('laterUploadInvoice')"
                            :description="
                                $translate('laterUploadInvoiceDescription')
                            "
                            :actions="[
                                {
                                    text: $translate('configureBillingaccount'),
                                    action: 'configure',
                                },
                            ]"
                            @configure="showBillingAccountSettings"
                        />
                    </template>
                    <template v-else-if="invoice_file_url && !parsing_invoice">
                        <iframe
                            :src="invoice_file_url"
                            class="w-100"
                            height="100%"
                            width="100%"
                        />
                    </template>
                    <template v-else>
                        <SelectFile
                            @input="invoiceFileSelected($event)"
                            :loading="parsing_invoice"
                            :dragText="$translate('dropInvoice')"
                            :placeholder-title="$translate('placeholderTitle')"
                            :placeholder-description="
                                $translate('placeholderDescription')
                            "
                            :placeholder-action-text="
                                $translate('placeholderActionText')
                            "
                            :loading-text="$translate('parsing')"
                        />
                    </template>
                </div>
                <div class="invoiceinfos invoice-editor">
                    <div class="stitch-modal stitch-modal--split">
                        <v-card class="stitch-modal__card" elevation="0"
                            ><v-card-text>
                                <h5 class="stitch-modal__title">
                                    <v-icon size="16"
                                        >mdi-file-document-outline</v-icon
                                    ><span>{{
                                        $translate("invoiceSettings")
                                    }}</span>
                                </h5>

                                <div class="invoice-settings-fields">
                                    <v-select
                                        hide-details="auto"
                                        dense
                                        outlined
                                        :value="invoice.credit"
                                        @input="updateCreditType($event)"
                                        :error-messages="
                                            getRequiredFieldErrors(
                                                'credit',
                                                invoice.credit
                                            )
                                        "
                                        :label="$translate('creditDebit')"
                                        :items="[
                                            {
                                                text: $translate('debit'),
                                                value: false,
                                            },
                                            {
                                                text: $translate('credit'),
                                                value: true,
                                            },
                                        ]"
                                    />
                                    <dl v-if="order" class="invoice-context">
                                        <div>
                                            <dt>
                                                {{ $translate("customer") }}
                                            </dt>
                                            <dd>
                                                {{
                                                    invoice.billed_data?.name ||
                                                    order.buyer_data?.name ||
                                                    "—"
                                                }}
                                            </dd>
                                        </div>
                                        <div>
                                            <dt>
                                                {{
                                                    $translate("orderOptional")
                                                }}
                                            </dt>
                                            <dd>
                                                {{
                                                    invoice.order_data
                                                        ?.order_id ||
                                                    order.order_id ||
                                                    "—"
                                                }}
                                            </dd>
                                        </div>
                                    </dl>
                                    <SearchCustomer
                                        hide-details="auto"
                                        v-if="!order"
                                        dense
                                        outlined
                                        class="mt-0"
                                        :value="invoice.billed"
                                        :disabled="!!order"
                                        :allow-create="true"
                                        :create-label="
                                            $translate('createCustomer')
                                        "
                                        @input="onCustomerInput"
                                        :error-messages="
                                            getRequiredFieldErrors(
                                                'billed',
                                                invoice.billed
                                            )
                                        "
                                        :label="$translate('customer')"
                                        required
                                    />
                                    <SearchBillingAccount
                                        hide-details="auto"
                                        dense
                                        outlined
                                        class="mt-0"
                                        v-if="isCustomerInvoice"
                                        :key="invoice.billingaccount"
                                        :value="invoice.billingaccount"
                                        @input="onBillingAccountInput"
                                        :error-messages="
                                            getRequiredFieldErrors(
                                                'billingaccount',
                                                invoice.billingaccount
                                            )
                                        "
                                        :label="$translate('billingAccount')"
                                        required
                                    />
                                    <SearchOrder
                                        hide-details="auto"
                                        v-if="!order"
                                        dense
                                        outlined
                                        class="mt-0"
                                        :disabled="!!order || !invoice.billed"
                                        :value="invoice.order"
                                        :additionalParams="{
                                            buyer: invoice.billed,
                                        }"
                                        @input="
                                            updateInvoiceData({
                                                key: 'order',
                                                value: $event?.id,
                                            });
                                            updateInvoiceData({
                                                key: 'order_data',
                                                value: $event,
                                            });
                                        "
                                        :error-messages="
                                            saving_invoice_errors?.order
                                        "
                                        :label="$translate('orderOptional')"
                                    />

                                    <DateTime
                                        hide-details="auto"
                                        dense
                                        outlined
                                        class="mt-0"
                                        :key="`invoice-date=${invoice.invoice_date}`"
                                        :value="invoice.invoice_date"
                                        @input="updateInvoiceDate($event)"
                                        :min-date="null"
                                        :max-date="invoice.due_date"
                                        :error-messages="
                                            getRequiredFieldErrors(
                                                'invoice_date',
                                                invoice.invoice_date
                                            )
                                        "
                                        :label="
                                            $translate(
                                                invoice.credit
                                                    ? 'creditNoteDateLabel'
                                                    : 'invoiceDateLabel'
                                            )
                                        "
                                        :only-date="true"
                                        :placeholder="
                                            invoice.invoice_date
                                                ? $translate(
                                                      invoice.credit
                                                          ? 'creditNoteDatePlaceholder'
                                                          : 'invoiceDatePlaceholder'
                                                  ).replace(
                                                      '{date}',
                                                      $moment(
                                                          invoice.invoice_date
                                                      ).format('LL')
                                                  )
                                                : null
                                        "
                                    />
                                    <v-select
                                        hide-details="auto"
                                        dense
                                        outlined
                                        v-if="!invoice.credit"
                                        class="mt-0"
                                        :disabled="!invoice.invoice_date"
                                        :value="payment_delay"
                                        @input="updatePaymentDelay($event)"
                                        :error-messages="
                                            saving_invoice_errors?.payment_delay
                                        "
                                        :label="
                                            $translate(
                                                'paymentDelays.paymentDelay'
                                            )
                                        "
                                        :items="paymentDelays"
                                        required
                                    />
                                    <DateTime
                                        hide-details="auto"
                                        dense
                                        outlined
                                        v-if="!invoice.credit"
                                        class="mt-0"
                                        :key="`due-date=${invoice.due_date}`"
                                        :value="invoice.due_date"
                                        @input="updateDueDate($event)"
                                        :min-date="invoice.invoice_date"
                                        :max-date="null"
                                        :error-messages="
                                            saving_invoice_errors?.due_date
                                        "
                                        :label="
                                            $translate('invoiceDueDateLabel')
                                        "
                                        :placeholder="
                                            invoice.due_date
                                                ? $translate(
                                                      'invoiceDueDatePlaceholder'
                                                  ).replace(
                                                      '{date}',
                                                      $moment(
                                                          invoice.due_date
                                                      ).format('LL')
                                                  )
                                                : null
                                        "
                                        :only-date="true"
                                    />
                                    <v-select
                                        hide-details="auto"
                                        dense
                                        outlined
                                        v-if="
                                            !invoice.credit && isCustomerInvoice
                                        "
                                        class="mt-0"
                                        :value="invoice.payment_method"
                                        :items="paymentMethodChoices"
                                        :label="$translate('paymentMethod')"
                                        :error-messages="
                                            saving_invoice_errors?.payment_method
                                        "
                                        clearable
                                        @input="onPaymentMethodChange"
                                    />
                                    <v-select
                                        hide-details="auto"
                                        dense
                                        outlined
                                        v-if="
                                            !invoice.credit &&
                                            isCustomerInvoice &&
                                            invoice.payment_method ===
                                                'transfer'
                                        "
                                        class="mt-0"
                                        :value="invoice.bank_account"
                                        :items="bankAccountChoices"
                                        item-text="text"
                                        item-value="value"
                                        :label="$translate('accountToCredit')"
                                        :error-messages="
                                            saving_invoice_errors?.bank_account
                                        "
                                        :loading="invoiceBankAccountsLoading"
                                        @input="onBankAccountInput"
                                    />
                                    <p
                                        v-if="missingTransferIban"
                                        class="text-caption mb-0"
                                    >
                                        {{ $translate("noIbanWillBePrinted") }}
                                    </p>
                                    <p
                                        v-if="
                                            emptyBankAccountList &&
                                            !canAddBankAccount
                                        "
                                        class="text-caption mb-0"
                                    >
                                        {{
                                            $translate(
                                                "addIbanOnBillingAccount",
                                                { name: billingAccountName }
                                            )
                                        }}
                                    </p>
                                    <p
                                        v-if="missingCustomerDebitIban"
                                        class="text-caption mb-0"
                                    >
                                        {{
                                            $translate(
                                                "customerDebitIbanMissing"
                                            )
                                        }}
                                    </p>

                                    <template v-if="isCustomerProviderInvoice">
                                        <v-text-field
                                            hide-details="auto"
                                            dense
                                            outlined
                                            :value="invoiceIdValue"
                                            :error-messages="
                                                saving_invoice_errors?.invoice_id
                                            "
                                            class="mt-1"
                                            :label="$translate('invoiceId')"
                                            @input="
                                                updateInvoiceData({
                                                    key: 'invoice_id',
                                                    value: $event,
                                                })
                                            "
                                        />

                                        <div class="d-block d-sm-flex mt-4">
                                            <v-text-field
                                                hide-details="auto"
                                                dense
                                                outlined
                                                class="mr-0 mr-sm-3"
                                                :value="
                                                    invoice.total_amount_excl_vat
                                                "
                                                @input="
                                                    updateInvoiceData({
                                                        key: 'total_amount_excl_vat',
                                                        value: parseFloat(
                                                            $utils.normalizeDecimalString(
                                                                $event
                                                            )
                                                        ),
                                                    })
                                                "
                                                :error-messages="
                                                    getRequiredFieldErrors(
                                                        'total_amount_excl_vat',
                                                        invoice.total_amount_excl_vat
                                                    )
                                                "
                                                :label="
                                                    $translate('totalExclVat')
                                                "
                                                append-icon="€"
                                                name="total_amount_excl_vat"
                                                type="text"
                                                inputmode="decimal"
                                                required
                                            />
                                            <v-text-field
                                                hide-details="auto"
                                                dense
                                                outlined
                                                class="ml-0 ml-sm-3"
                                                :value="
                                                    invoice.total_vat_amount
                                                "
                                                @input="
                                                    updateInvoiceData({
                                                        key: 'total_vat_amount',
                                                        value: parseFloat(
                                                            $utils.normalizeDecimalString(
                                                                $event
                                                            )
                                                        ),
                                                    })
                                                "
                                                :error-messages="
                                                    getRequiredFieldErrors(
                                                        'total_vat_amount',
                                                        invoice.total_vat_amount
                                                    )
                                                "
                                                :label="$translate('totalVat')"
                                                append-icon="€"
                                                name="total_vat_amount"
                                                type="text"
                                                inputmode="decimal"
                                                required
                                            />
                                        </div>
                                        <div class="invoice-total">
                                            <span>{{
                                                $translate("totalInclVat")
                                            }}</span>
                                            <strong>{{
                                                formatInvoiceAmount(
                                                    invoice.total_amount_incl_vat ||
                                                        0
                                                )
                                            }}</strong>
                                        </div>
                                        <v-messages
                                            class="error--text"
                                            :value="
                                                [].concat(
                                                    saving_invoice_errors?.total_amount_incl_vat ||
                                                        []
                                                )
                                            "
                                        />
                                    </template>
                                    <v-textarea
                                        hide-details="auto"
                                        dense
                                        outlined
                                        v-if="isCustomerInvoice"
                                        :value="invoice.notes"
                                        @input="
                                            updateInvoiceData({
                                                key: 'notes',
                                                value: $event,
                                            })
                                        "
                                        :error-messages="
                                            saving_invoice_errors?.notes
                                        "
                                        class="mt-0"
                                        :label="$translate('notes')"
                                        :placeholder="
                                            $translate('notesPlaceholder')
                                        "
                                    />
                                </div> </v-card-text
                        ></v-card>
                    </div>
                </div>
            </div>
        </template>
        <template slot="bottom">
            <Button
                :text="$translate('cancel')"
                class="my-2 my-sm-4 w-100 w-sm-auto"
                ghost
                @click="hideModal"
            />
            <Button
                class="my-2 my-sm-4 w-100 w-sm-auto ml-sm-3"
                :text="isNew ? $translate('create') : $translate('save')"
                :disabled="!canSaveInvoice()"
                :loading="saving_invoice"
                @click="saveInvoiceClicked"
            />
        </template>
    </ModalContent>
</template>

<script>
import { mapState, mapMutations, mapActions, mapGetters } from "vuex";
import axios from "@/plugins/axios";
import GlobalMixins from "@/mixins/global";
import { formatAmount } from "@/utils/numberFormat";
import billingaccounts from "@/stores/submodules/billingaccounts";
import BankAccountModal from "@/modals/bank-account.vue";

const ADD_BANK_ACCOUNT_VALUE = "__add_bank_account__";

export default {
    name: "CustomerInvoiceModal",
    mixins: [GlobalMixins],
    maxWidth: "80%",
    maxHeight: "80%",
    width: "100%",
    scrollable: false,
    props: {
        params: {
            type: Object,
            required: true,
        },
    },
    data() {
        return {
            step: "validate",
            invoiceBankAccounts: [],
            invoiceBankAccountsLoading: false,
            bankAccountsRequestId: 0,
        };
    },
    computed: {
        ...mapGetters("editcustomerinvoice", ["dueDate"]),
        ...mapState("editcustomerinvoice", [
            "billingaccount_loading",
            "billingaccount",
            "invoice_file_url",
            "invoice_type",
            "invoice",
            "order",
            "parsing_invoice",
            "payment_delay",
            "saving_invoice_errors",
            "saving_invoice",
        ]),
        invoiceIdValue() {
            if (this.isCustomerProviderInvoice) {
                return this.invoice.invoice_id;
            }
            if (this.billingaccount?.handle_invoices) {
                return this.$translate("autogenerated");
            }
            return this.invoice.invoice_id;
        },
        paymentDelays() {
            let delays = [
                { text: this.$translate("paymentDelays.selectDelay"), value: null },
                { text: this.$translate("paymentDelays.0days"), value: 0 },
                {
                    text: this.$translate("paymentDelays.30days"),
                    value: 30,
                },
                {
                    text: this.$translate("paymentDelays.30daysendofmonth"),
                    value: "30daysendofmonth",
                },
                {
                    text: this.$translate("paymentDelays.60days"),
                    value: 60,
                },
                {
                    text: this.$translate("paymentDelays.60daysendofmonth"),
                    value: "60daysendofmonth",
                },
                {
                    text: this.$translate("paymentDelays.90days"),
                    value: 90,
                },
            ];
            let appendItem = function (value) {
                if (!delays.find((delay) => delay.value === value)) {
                    delays.push({
                        text: `${value} ${this.$translate(
                            "paymentDelays.days"
                        )} (${this.$translate("paymentDelays.default")})`,
                        value: value,
                    });
                }
            };
            if (
                Number.isInteger(
                    this.invoice.billed_data?.default_payment_delay
                )
            ) {
                appendItem.call(
                    this,
                    this.invoice.billed_data.default_payment_delay
                );
            } else {
                if (
                    Number.isInteger(this.billingaccount?.default_payment_delay)
                ) {
                    appendItem.call(
                        this,
                        this.billingaccount.default_payment_delay
                    );
                }
            }
            delays = delays.sort((a, b) => a.value - b.value);
            delays.push({
                text: this.$translate("paymentDelays.choose"),
                value: null,
            });
            return delays;
        },
        isCustomerInvoice() {
            return this.invoice_type === "customerinvoice";
        },
        paymentMethodChoices() {
            const all = [
                { text: this.$translate("paymentMethods.unset"), value: null },
                { text: this.$translate("paymentMethods.transfer"), value: "transfer" },
                { text: this.$translate("paymentMethods.direct_debit"), value: "direct_debit" },
                { text: this.$translate("paymentMethods.bill_of_exchange"), value: "bill_of_exchange" },
                { text: this.$translate("paymentMethods.check"), value: "check" },
                { text: this.$translate("paymentMethods.card"), value: "card" },
                { text: this.$translate("paymentMethods.cash"), value: "cash" },
            ];
            const enabled = this.enabledPaymentMethodsForInvoice;
            const current = this.invoice?.payment_method || null;
            return all.filter(
                (item) =>
                    item.value === null ||
                    enabled.includes(item.value) ||
                    item.value === current
            );
        },
        enabledPaymentMethodsForInvoice() {
            const enabled = this.billingaccount?.enabled_payment_methods;
            if (Array.isArray(enabled)) {
                return enabled;
            }
            return [
                "transfer",
                "direct_debit",
                "bill_of_exchange",
                "check",
                "card",
                "cash",
            ];
        },
        bankAccountChoices() {
            const items = this.invoiceBankAccounts.map((account) => ({
                text: this.bankAccountOptionLabel(account),
                value: account.id,
            }));
            if (!items.length && this.canAddBankAccount) {
                items.push({
                    text: this.$translate("addBankAccount"),
                    value: ADD_BANK_ACCOUNT_VALUE,
                });
            }
            return items;
        },
        canAddBankAccount() {
            return (
                !!this.invoiceBillingAccountId &&
                this.userHasPermission("billing.change_billingaccount")
            );
        },
        billingAccountName() {
            return this.billingaccount?.name || "";
        },
        invoiceBillingAccountId() {
            return this.invoice?.billingaccount || null;
        },
        missingTransferIban() {
            return (
                this.isCustomerInvoice &&
                !this.invoice?.credit &&
                this.invoice?.payment_method === "transfer" &&
                !this.invoice?.bank_account
            );
        },
        emptyBankAccountList() {
            return (
                this.missingTransferIban &&
                !!this.invoice?.billingaccount &&
                !this.invoiceBankAccountsLoading &&
                this.invoiceBankAccounts.length === 0
            );
        },
        missingCustomerDebitIban() {
            const billed = this.invoice?.billed_data;
            const hasDebitIban = !!(billed?.debit_iban || billed?.debit_iban_masked);
            return (
                this.isCustomerInvoice &&
                !this.invoice?.credit &&
                this.invoice?.payment_method === "direct_debit" &&
                !hasDebitIban
            );
        },
        isCustomerProviderInvoice() {
            return this.invoice_type === "customerproviderinvoice";
        },
        canEditInvoiceId() {
            if (this.isCustomerProviderInvoice) {
                return true;
            }
            if (this.billingaccount?.handle_invoices) {
                return false;
            }
            return true;
        },
        isNew() {
            return !this.invoice?.id;
        },
        isPhone() {
            return this.$vuetify.breakpoint.xsOnly;
        },
        isSplitLayout() {
            return this.$vuetify.breakpoint.mdAndUp;
        },
        hasFilePreview() {
            return !!this.invoice_file_url && !this.parsing_invoice;
        },
        /**
         * Every customer invoice branch of the document zone is a decorative
         * placeholder: only the other invoice types show the file itself or
         * the upload affordance, which are worth their height on a phone.
         */
        showDocument() {
            return !this.isPhone || !this.isCustomerInvoice;
        },
        title() {
            return this.isNew
                ? this.$translate("createInvoice")
                : this.$translate("editInvoice");
        },
        displayLabel() {
            return this.$vuetify.breakpoint.name != "xs";
        },
    },
    created() {
        this.init(this.params);
    },
    watch: {
        invoiceBillingAccountId: {
            immediate: true,
            handler(billingaccountId) {
                this.fetchInvoiceBankAccounts(billingaccountId);
            },
        },
    },
    methods: {
        formatInvoiceAmount(value) {
            return formatAmount(value, { forceDecimals: true });
        },
        ...mapActions("editcustomerinvoice", [
            "saveInvoice",
            "init",
            "parseInvoiceFile",
            "changeCustomer",
        ]),
        ...mapMutations("editcustomerinvoice", [
            "updateInvoiceData",
            "updateGenerateInvoice",
            "updateInvoiceFile",
            "updatePaymentDelay",
            "updateInvoiceDate",
            "updateDueDate",
            "updateBillingAccount",
            "forceUpdateInvoiceWithParsedData",
        ]),
        updateCreditType(value) {
            this.updateInvoiceData({
                key: "credit",
                value: value,
            });
            // Si on passe à credit=true, on met due_date = invoice_date
            if (value && this.invoice.invoice_date) {
                this.updateInvoiceData({
                    key: "due_date",
                    value: this.invoice.invoice_date,
                });
            }
            if (value) {
                this.updateInvoiceData({ key: "payment_method", value: null });
                this.updateInvoiceData({ key: "bank_account", value: null });
            }
        },
        formatIban(value) {
            if (!value) {
                return "";
            }
            return String(value)
                .replace(/\s+/g, "")
                .toUpperCase()
                .replace(/(.{4})/g, "$1 ")
                .trim();
        },
        bankAccountOptionLabel(account) {
            const name = account.label || account.bank_name || "";
            const iban = this.formatIban(account.iban);
            const parts = [name, iban].filter(Boolean);
            let text = parts.join(" — ");
            if (account.is_default) {
                text = `${text} (${this.$translate("defaultAccount")})`;
            }
            return text;
        },
        defaultBankAccountId() {
            const account = this.invoiceBankAccounts.find((item) => item.is_default);
            return account ? account.id : null;
        },
        billingAccountId(value) {
            if (value && typeof value === "object") {
                return value.id || null;
            }
            return value || null;
        },
        async fetchInvoiceBankAccounts(billingaccountId) {
            const id = this.billingAccountId(billingaccountId);
            const requestId = (this.bankAccountsRequestId || 0) + 1;
            this.bankAccountsRequestId = requestId;
            if (!id) {
                this.invoiceBankAccounts = [];
                return;
            }
            this.invoiceBankAccountsLoading = true;
            try {
                const response = await axios.get(
                    `/api/billingaccounts/${id}/bankaccounts/`,
                    { params: { limit: 200 } }
                );
                if (requestId !== this.bankAccountsRequestId) {
                    return;
                }
                const data = response.data;
                this.invoiceBankAccounts = Array.isArray(data) ? data : data.results || [];
                this.selectDefaultBankAccount();
            } catch (error) {
                if (requestId !== this.bankAccountsRequestId) {
                    return;
                }
                this.invoiceBankAccounts = [];
            } finally {
                if (requestId === this.bankAccountsRequestId) {
                    this.invoiceBankAccountsLoading = false;
                }
            }
        },
        selectDefaultBankAccount() {
            if (this.invoice?.payment_method !== "transfer") {
                return;
            }
            const stillSelected = this.invoiceBankAccounts.some(
                (account) => account.id === this.invoice.bank_account
            );
            if (!stillSelected) {
                this.updateInvoiceData({
                    key: "bank_account",
                    value: this.defaultBankAccountId(),
                });
            }
        },
        onPaymentMethodChange(value) {
            const method = value || null;
            this.updateInvoiceData({ key: "payment_method", value: method });
            if (method === "transfer") {
                if (!this.invoiceBankAccounts.length) {
                    this.fetchInvoiceBankAccounts(this.invoice?.billingaccount);
                    return;
                }
                this.selectDefaultBankAccount();
            } else {
                this.updateInvoiceData({ key: "bank_account", value: null });
            }
        },
        async onBankAccountInput(value) {
            if (value === ADD_BANK_ACCOUNT_VALUE) {
                await this.openAddBankAccountModal();
                return;
            }
            this.updateInvoiceData({ key: "bank_account", value });
        },
        async openAddBankAccountModal() {
            const billingaccountId = this.billingAccountId(
                this.invoice?.billingaccount
            );
            if (!billingaccountId || !this.canAddBankAccount) {
                return;
            }
            const account = await this.showModal({
                component: BankAccountModal,
                params: {
                    billingaccountId,
                    billingaccountName: this.invoice?.billingaccount?.name,
                    bankAccount: null,
                    isFirstAccount: this.invoiceBankAccounts.length === 0,
                },
            });
            await this.fetchInvoiceBankAccounts(billingaccountId);
            if (account?.id) {
                this.updateInvoiceData({
                    key: "bank_account",
                    value: account.id,
                });
            }
        },
        async onCustomerInput(customer) {
            const previousId = this.invoice?.billed || null;
            const nextId = customer?.id || null;
            await this.changeCustomer(customer);
            if (nextId === previousId) {
                return;
            }
            const method = customer?.preferred_payment_method || null;
            this.updateInvoiceData({ key: "payment_method", value: method });
            await this.fetchInvoiceBankAccounts(this.invoice?.billingaccount);
            this.updateInvoiceData({
                key: "bank_account",
                value: method === "transfer" ? this.defaultBankAccountId() : null,
            });
        },
        async onBillingAccountInput(account) {
            const nextId = account?.id || null;
            const previousId = this.invoice?.billingaccount || null;
            this.updateInvoiceData({
                key: "billingaccount",
                value: nextId,
            });
            this.updateBillingAccount(account);
            if (nextId === previousId) {
                return;
            }
            await this.fetchInvoiceBankAccounts(nextId);
            if (this.invoice.payment_method === "transfer") {
                this.updateInvoiceData({
                    key: "bank_account",
                    value: this.defaultBankAccountId(),
                });
            } else if (this.invoice.bank_account) {
                this.updateInvoiceData({ key: "bank_account", value: null });
            }
        },
        showBillingAccountSettings() {
            this.routerPush({
                name: "billingaccount",
                params: { billingaccount_id: this.billingaccount.id },
            });
            this.hideModal();
        },
        canSaveInvoice() {
            if (this.invoice.biller) {
                if (!this.billingaccount) {
                    return false;
                }
                return (
                    !!this.invoice.billed &&
                    !!this.invoice.invoice_date &&
                    !this.parsing_invoice
                );
            }
            if (this.isCustomerProviderInvoice && !this.invoice_file_url) {
                return false;
            }
            if (this.isCustomerProviderInvoice && !this.invoice_id) {
                return false;
            }
            return (
                !!this.invoice.billed &&
                !!this.invoice.invoice_date &&
                !!this.invoice.due_date &&
                !this.parsing_invoice
            );
        },
        getRequiredFieldErrors(fieldName, fieldValue) {
            const serverErrors = this.saving_invoice_errors?.[fieldName];
            // If there are server errors, return them (don't add required message)
            if (serverErrors && serverErrors.length > 0) {
                return serverErrors;
            }
            // If field is empty and highlight is enabled, show required message
            const highlightRequired = this.params?.highlightRequired !== false && (!this.isNew || Object.keys(this.saving_invoice_errors || {}).length);
            if (highlightRequired && (fieldValue === null || fieldValue === undefined || fieldValue === "")) {
                return [this.$translate("messages.required")];
            }
            return serverErrors || null;
        },
        async invoiceFileSelected(invoice_file) {
            try {
                const mismatch = await this.parseInvoiceFile({
                    invoice_file: invoice_file,
                });
                if (mismatch) {
                    const resultSwal = await this.$swal({
                        title: this.$translate("invoiceInfoMismatch"),
                        text: this.$translate("invoiceInfoMismatchDescription"),
                        type: "question",
                        showCancelButton: false,
                        showCloseButton: true,
                    });
                    return;
                }
                this.forceUpdateInvoiceWithParsedData();
            } catch (error) {
                this.$swal({
                    title: this.$translate("error"),
                    html: this.$utils.resultToSwalContent(this, error),
                    type: "error",
                });
            }
        },
        async saveInvoiceClicked() {
            try {
                const result = await this.saveInvoice();
                this.addAlert(
                    this.isNew
                        ? this.$translate("invoiceCreated")
                        : this.$translate("invoiceUpdated")
                );
                this.hideModal({ result });
            } catch (error) {
                this.$swal({
                    title: this.$translate("error"),
                    html: this.$utils.resultToSwalContent(this, error),
                    type: "error",
                });
            }
        },
    },
    i18n: {
        messages: {
            en: {
                automaticInvoice: "Automatic invoice",
                automaticInvoiceDescription:
                    "The invoice will be automatically generated by Safetee.",
                autogenerated: "Auto-generated",
                configure:
                    "If you want to configure your billing account to generate invoices automatically, you can configure it ",
                configureBillingaccount: "Configure",
                create: "Create invoice",
                createCustomer: "Create customer",
                createInvoice: "Create invoice",
                dropInvoice: "Drop your invoice here",
                editInvoice: "Edit invoice",
                here: "here",
                invoiceCreated: "Invoice created",
                invoiceDateLabel: "Invoice date",
                invoiceDatePlaceholder: "Confirm the date: {date}",
                invoiceDueDateLabel: "Invoice due date",
                paymentMethod: "Payment method",
                accountToCredit: "Account to credit",
                addBankAccount: "Add a bank account",
                defaultAccount: "Default",
                noIbanWillBePrinted: "No IBAN will be printed on this invoice.",
                customerDebitIbanMissing: "The customer's debit IBAN is not set.",
                addIbanOnBillingAccount: "Add an IBAN on billing account {name}.",
                paymentMethods: {
                    unset: "Not set",
                    transfer: "Wire transfer",
                    direct_debit: "Direct debit",
                    bill_of_exchange: "Accepted bill of exchange",
                    check: "Check",
                    card: "Card terminal",
                    cash: "Cash",
                },
                invoiceDueDatePlaceholder: "Confirm the date: {date}",
                invoiceUpdated: "Invoice updated",
                laterUploadInvoice: "Later upload invoice",
                laterUploadInvoiceDescription:
                    "You will be able to upload the invoice later.",
                parsing: "Extracting invoice data...",
                paymentDelays: {
                    choose: "Choose a date",
                    days: "days",
                    default: "default",
                    "0days": "0 days",
                    "30days": "30 days",
                    "30daysendofmonth": "30 days end of month",
                    "60days": "60 days",
                    "60daysendofmonth": "60 days end of month",
                    "90days": "90 days",
                    "90daysendofmonth": "90 days end of month",
                    paymentDelay: "Payment delay",
                    selectDelay: "Apply a delay",
                },
                placeholderActionText: "Select a file",
                placeholderDescription: "Please select a file",
                placeholderTitle: "No file selected",
                save: "Save invoice",
                selectCustomer: "Select customer",
                selectCustomerDescription:
                    "Please select the customer to bill.",
                invoiceInfoMismatch: "Invoice information mismatch",
                invoiceInfoMismatchDescription:
                    "The invoice information extracted from the file does not match the information already entered in the form. Do you want to update the form with the extracted information?",
                errorUploadingFile: "Error uploading file",
                orderOptional: "Order (optional)",
                invoiceSettings: "Invoice settings",
                customer: "Customer",
                billingAccount: "Billing account",
                invoiceId: "Invoice ID",
                totalExclVat: "Total excl. VAT (*)",
                totalVat: "Total VAT (*)",
                totalInclVat: "Total incl. VAT (*)",
                notes: "Notes",
                notesPlaceholder: "Enter notes here that will be displayed on the invoice",
                cancel: "Cancel",
                yes: "Yes",
                no: "No",
                error: "Error",
                messages: {
                    required: "Required",
                },
                creditNoteDateLabel: "Credit note date",
                creditNoteDatePlaceholder: "Confirm the date: {date}",
                creditDebit: "Credit / debit",
                debit: "Debit",
                credit: "Credit",
            },
            fr: {
                automaticInvoice: "Facture automatique",
                automaticInvoiceDescription:
                    "La facture sera automatiquement générée par Safetee.",
                autogenerated: "Généré automatiquement",
                configure:
                    "Si vous désirez que Safetee génère vos factures, vous pouvez configurer votre compte de facturation ",
                configureBillingaccount: "Configurer",
                create: "Créer la facture",
                createCustomer: "Créer un client",
                createInvoice: "Créer une facture",
                dropInvoice: "Déposez votre facture ici",
                editInvoice: "Modifier la facture",
                here: "ici",
                invoiceCreated: "Facture créée",
                invoiceDateLabel: "Date de la facture",
                invoiceDatePlaceholder: "Confirmez la date : {date}",
                invoiceDueDateLabel: "Date d'échéance de la facture",
                paymentMethod: "Mode de règlement",
                accountToCredit: "Compte à créditer",
                addBankAccount: "Ajouter un compte bancaire",
                defaultAccount: "Par défaut",
                noIbanWillBePrinted: "Aucun IBAN ne sera imprimé sur cette facture.",
                customerDebitIbanMissing: "L'IBAN de prélèvement du client n'est pas renseigné.",
                addIbanOnBillingAccount: "Ajoutez un IBAN sur le compte de facturation {name}.",
                paymentMethods: {
                    unset: "Non renseigné",
                    transfer: "Virement",
                    direct_debit: "Prélèvement",
                    bill_of_exchange: "Effet accepté",
                    check: "Chèque",
                    card: "TPE",
                    cash: "Espèces",
                },
                invoiceDueDatePlaceholder: "Confirmez la date : {date}",
                invoiceUpdated: "Facture mise à jour",
                laterUploadInvoice: "Facturation externe",
                laterUploadInvoiceDescription:
                    "Le fichier de la facture devra être téléchargé manuellement lors de la validation de la facture.",
                parsing: "Extraction des données de la facture...",
                paymentDelays: {
                    choose: "Choisir un délai",
                    days: "jours",
                    default: "par défaut",
                    "0days": "0 jours",
                    "30days": "30 jours",
                    "30daysendofmonth": "30 jours fin de mois",
                    "60days": "60 jours",
                    "60daysendofmonth": "60 jours fin de mois",
                    "90days": "90 jours",
                    "90daysendofmonth": "90 jours fin de mois",
                    paymentDelay: "Délai de paiement",
                    selectDelay: "Appliquer un délai",
                },
                placeholderActionText: "Sélectionner un fichier",
                placeholderDescription:
                    "Veuillez sélectionner le fichier de la facture",
                placeholderTitle: "Aucun fichier sélectionné",
                save: "Sauvegarder la facture",
                selectCustomer: "Sélectionner le client",
                selectCustomerDescription:
                    "Veuillez sélectionner le client à facturer.",
                invoiceInfoMismatch:
                    "Incohérence des informations de la facture",
                invoiceInfoMismatchDescription:
                    "Les totaux extraits du fichier ne correspondent pas aux informations de la facture. Veuillez modifier votre facture pour les faire correspondre et ré-essayer ou mettre en ligne un autre fichier.",
                errorUploadingFile: "Erreur lors de l'envoi du fichier",
                orderOptional: "Commande (optionnel)",
                invoiceSettings: "Paramètres de facture",
                customer: "Client",
                billingAccount: "Compte de facturation",
                invoiceId: "ID Facture",
                totalExclVat: "Total HT (*)",
                totalVat: "Total TVA (*)",
                totalInclVat: "Total TTC (*)",
                notes: "Notes",
                notesPlaceholder: "Entrez les notes ici qui seront affichées sur la facture",
                cancel: "Annuler",
                yes: "Oui",
                no: "Non",
                error: "Erreur",
                messages: {
                    required: "Obligatoire",
                },
                creditNoteDateLabel: "Date de l'avoir",
                creditNoteDatePlaceholder: "Confirmez la date : {date}",
                creditDebit: "Facture / avoir",
                debit: "Facture",
                credit: "Avoir",
            },
        },
    },
};
</script>

<style lang="scss" scoped>
@import "@/assets/css/_stitch-form.scss";
@import "@/assets/css/mixins.scss";
@import "@/assets/css/invoice-modal.scss";

.customerinvoice-layout {
    display: flex;
    flex-direction: column;
    height: 100%;
    width: 100%;
    min-width: 0;
    min-height: 0;
}

.invoiceinfos {
    width: 100%;
    min-width: 0;
    flex: 1 1 auto;
    min-height: 0;
}

.invoice-settings-fields {
    display: grid;
    grid-template-columns: minmax(0, 1fr);
    gap: 16px;

    // Space component roots equally, including date and search wrappers.
    > * {
        margin-top: 0 !important;
        margin-bottom: 0 !important;
        min-width: 0;
    }
}

.document {
    flex: 0 0 auto;
    max-height: 280px;
    min-width: 0;
    min-height: 0;
    overflow: hidden;
    background: #f5f5f5;
    padding: 20px;
    margin-bottom: 16px;
}

/* Stacked layout (below md): the modal body owns the scroll, and the
   upload zone uses the compact affordance as soon as the document is
   no longer beside the fields. */
@include under-to(md) {
    .customerinvoice-layout {
        height: auto;
    }

    .invoiceinfos {
        flex: 0 0 auto;
        overflow: visible;
    }

    .document.document--upload {
        height: auto;
        max-height: none;
        padding: 12px;
        margin-bottom: 16px;
    }

    .document--upload ::v-deep .placeholder {
        min-height: 0 !important;
    }

    .document--upload ::v-deep .drop-zone {
        min-height: 0;
    }

    .document--upload ::v-deep .placeholder__icon-wrap {
        display: none !important;
    }

    .document--upload ::v-deep .placeholder--title {
        margin-top: 0;
    }
}

@include respond-to(xs) {
    .invoiceinfos {
        order: 1;
    }

    /* The file preview is secondary information: it goes under the fields,
       with a definite height so the iframe still has something to fill. */
    .document {
        order: 2;
        height: 200px;
        max-height: 200px;
        padding: 12px;
        margin-top: 16px;
        margin-bottom: 0;
    }

    .document--upload {
        order: 0;
        margin-top: 0;
        margin-bottom: 16px;
    }
}

@media (min-width: 960px) {
    .customerinvoice-layout {
        flex-direction: row;
    }

    .document {
        flex: 1 1 auto;
        max-height: none;
        min-height: 0;
        margin-bottom: 0;
        margin-right: 0;
    }

    .customerinvoice-layout--no-document .invoiceinfos {
        width: 100%;
        max-width: 100%;
    }

    .invoiceinfos {
        width: 450px;
        max-width: 50%;
        min-width: 300px;
        flex: 0 0 auto;
        overflow-x: hidden;
        overflow-y: auto;
    }
}

.invoiceinfos.invoice-editor ::v-deep .v-text-field--outlined .v-label { font-size: 11px; }
.invoiceinfos.invoice-editor { background: #eef2f7; padding: 16px; }
.invoiceinfos.invoice-editor ::v-deep .v-text-field--outlined:has(.v-label) { margin-top: 22px !important; }
.invoiceinfos.invoice-editor .invoice-settings-fields > * { margin-bottom: 0 !important; }
.invoiceinfos.invoice-editor .stitch-modal__card { flex-shrink: 0; }
</style>
